<template>
  <vetster-input-group
    :label="label"
    :feedback="feedback || null"
    :error="error"
    :shake="shake"
    :optional="optional"
    :is-private="isPrivate"
  >
    <div v-if="isUpdating">
      <div class="flex justify-start items-center -mx-1">
        <span class="w-1/4 px-1">
          <vetster-select
            ref="year"
            v-model.trim="inputs.year"
            name="year"
            is-rebranded
            :disabled="loading"
            :error="error"
            :options="getYearOptions(1960)"
            :class="currentBaseClass"
          />
        </span>
        <span class="w-1/2 px-1">
          <vetster-select
            ref="month"
            v-model.trim="inputs.month"
            name="month"
            is-rebranded
            :error="error"
            :disabled="loading"
            :options="monthOptions"
            :class="currentBaseClass"
          />
        </span>
        <span class="w-1/4 px-1">
          <vetster-select
            ref="day"
            v-model.trim="inputs.day"
            name="day"
            is-rebranded
            :class="currentBaseClass"
            :disabled="loading"
            :error="error"
            :options="dayOptions"
          />
        </span>
      </div>
    </div>
    <div v-else>
      <div class="flex justify-start items-center -mx-1">
        <span class="w-1/2 px-1">
          <vetster-input
            ref="unitValue"
            v-model.number="inputs.value"
            type="number"
            min="1"
            maxlength="3"
            :max="125"
            :disabled="loading"
            :error="error"
            @input="setUnitValue"
          />
        </span>
        <span class="w-1/2 px-1">
          <vetster-select
            ref="unit"
            v-model.trim="inputs.unit"
            name="unit"
            :disabled="loading"
            :error="error"
            :options="[
              { value: 'YEARS', text: $tc('units.years', inputs.value) },
              { value: 'MONTHS', text: $tc('units.months', inputs.value) },
            ]"
            :class="currentBaseClass"
          />
        </span>
      </div>
    </div>
  </vetster-input-group>
</template>

<script>
import range from 'lodash/range';
import { DateTime } from 'luxon';
import { mapGetters } from 'vuex';

import VetsterInputGroup from '@/brand-components/VetsterInputGroup';
import VetsterSelect from '@/brand-components/VetsterSelect';
import useFormHelpers from '@/composables/use-form-helpers';
import { isValidDate } from '@/helpers/dateUtils';
import CommonAttributes from '@/mixins/CommonAttributes';

export default {
  name: 'VetsterSimpleBirthdayPicker',

  components: {
    VetsterInputGroup,
    VetsterSelect,
  },

  mixins: [CommonAttributes],

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  props: {
    error: {
      type: Boolean,
      default: false,
    },
    feedback: {
      type: String,
      default: null,
    },
    isPrivate: {
      type: Boolean,
      default: false,
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: null,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    pet: {
      type: Object,
      default: undefined,
    },
    shake: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: null,
    },
  },

  setup() {
    const {
      getMonthOptions,
      getYearOptions,
    } = useFormHelpers();

    return {
      getMonthOptions,
      getYearOptions,
    };
  },

  data() {
    return {
      loading: false,
      date: undefined,
      inputs: {
        year: undefined,
        month: undefined,
        day: undefined,
        unit: 'YEARS',
        value: undefined,
      },
    };
  },

  computed: {
    ...mapGetters(['getSiteLocale']),

    currentBaseClass() {
      return {
        'opacity-50': this.disabled,
        'border-red-300 text-red-800 placeholder-red-300 focus:border-red-300 focus:ring-red-200 focus:ring-2': this.error,
      };
    },

    dayOptions() {
      return this.prependBlankOption(range(1, this.daysInMonth + 1).map((value) => ({
        value,
        text: value,
      })));
    },

    daysInMonth() {
      if (this.inputs.month) {
        return new Date(this.inputs.year, this.inputs.month, 0).getDate();
      }

      return 28;
    },

    isMissingInput() {
      return (
        this.isUpdating && !this.inputs.year
      ) || (
        !this.isUpdating && !this.inputs.value
      );
    },

    monthOptions() {
      return this.prependBlankOption(this.getMonthOptions(this.getSiteLocale));
    },
  },

  watch: {
    inputs: {
      deep: true,
      handler() {
        this.emitDate();
      },
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.isUpdating) {
        this.setDate(this.pet?.birthday_on || DateTime.now().minus({ days: 1 }));
      }
    });
  },

  methods: {
    approximateDateFromInputs() {
      const now = DateTime.now().startOf('day');

      if (this.inputs.unit === 'MONTHS') {
        return now.minus({ months: this.inputs.value });
      }

      return now.minus({ years: this.inputs.value });
    },

    // Since we are creating the date with start of the month with utc zone,
    // other zones will affect like having a different month value.
    // Maximum possible difference between times on Earth is 26 hours.
    exactDateFromInputs() {
      if (!this.inputs.month) {
        return DateTime.fromObject({
          year: this.inputs.year,
          month: 1,
          day: 1,
          hour: 2,
          minute: 0,
        });
      }

      if (!this.inputs.day) {
        return DateTime.fromObject({
          year: this.inputs.year,
          month: this.inputs.month,
          day: 1,
          hour: 2,
          minute: 0,
        });
      }

      return DateTime.fromObject({
        year: this.inputs.year,
        month: this.inputs.month,
        day: this.inputs.day,
        hour: 2,
        minute: 0,
      });
    },

    emitDate() {
      let value;

      if (!this.isMissingInput) {
        const date = this.isUpdating
          ? this.exactDateFromInputs()
          : this.approximateDateFromInputs();

        if (isValidDate(date.toJSDate())) {
          value = date.toISODate();
        }
      }

      this.$emit('update:modelValue', value);
    },

    prependBlankOption(arr) {
      arr.unshift({
        value: '',
        text: '',
      });

      return arr;
    },

    setDate(birthdayOn) {
      if (!birthdayOn) {
        return;
      }

      const date = DateTime.fromISO(birthdayOn, { zone: 'utc' });
      this.inputs.year = date.year;
      this.inputs.month = date.month;
      this.inputs.day = date.day;
    },

    setUnitValue() {
      let { value } = this.inputs;

      if (value) {
        if (value > 99) {
          value = 99;
        }

        this.inputs.value = parseInt(value, 10);
      }
    },
  },
};
</script>

<style scoped>
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
</style>
